/**
 * @fileoverview Container component - Layout component
 * 
 * A responsive container component with max-width constraints.
 * Provides consistent page width across the application.
 * 
 * @module ui/shared/layout/Container
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Container variant definitions using CVA
 */
const containerVariants = cva("mx-auto w-full", {
  variants: {
    size: {
      sm: "max-w-screen-sm",
      md: "max-w-screen-md",
      lg: "max-w-screen-lg",
      xl: "max-w-screen-xl",
      "2xl": "max-w-screen-2xl",
      full: "max-w-full",
    },
    padding: {
      none: "px-0",
      sm: "px-4",
      md: "px-6",
      lg: "px-8",
      xl: "px-12",
    },
  },
  defaultVariants: {
    size: "xl",
    padding: "md",
  },
});

/**
 * Container component props
 */
export interface ContainerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof containerVariants> {}

/**
 * Container component
 * 
 * A responsive container with max-width and padding variants.
 * 
 * @param {ContainerProps} props - Container props
 * @returns {JSX.Element} Container element
 * 
 * @example
 * <Container size="lg" padding="lg">
 *   <YourContent />
 * </Container>
 */
const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  ({ className, size, padding, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(containerVariants({ size, padding }), className)}
        {...props}
      />
    );
  }
);

Container.displayName = "Container";

export { Container, containerVariants };

